function $q(ctx, selector, callback) {
  ctx.querySelectorAll(selector).forEach((el, index) => {
    callback(el, index, ctx)
  })
}

$q.first = function first(ctx, selector, callback) {
  const el = ctx.querySelector(selector)
  if (el) callback(el, 0, ctx)
}

// Cached query selector
function $cqs(selector, context = document) {
  // Check if the selector is already cached for this context
  if (!$cqs.cache) {
    // If not, create a cache object
    $cqs.cache = new WeakMap()
  }

  // Check if the selector is already in the cache for this context
  if (!$cqs.cache.has(context)) {
    $cqs.cache.set(context, new Map())
  }
  const cache = $cqs.cache.get(context)
  if (!cache.has(selector)) {
    // If not, query the DOM for the context and store the result in the cache
    cache.set(selector, context.querySelector(selector))
  }

  // Return the cached value
  return cache.get(selector)
}

const _HYDRATE_SKIP = Symbol('SKIP')
const FN_ATTR_EFFECTS_LOOKUP = {
  'x-show': (el, result) => {
    const show = !!result
    el.toggleAttribute('hidden', !show)
    // el.style.display = show ? '' : 'none'
  },
  'x-text': (el, result) => {
    el.textContent = result == null ? '' : result
  },
  'x-effect': null,
}
const SUPPORTED_KEYS = Object.keys(FN_ATTR_EFFECTS_LOOKUP)

const _children_cache = new WeakMap()
export const hydrateFromHtml = ({
  container,
  preferCachedChildren = true,
  // tuple of params and args
  context,
}) => {
  const fnAttrsLookup = FN_ATTR_EFFECTS_LOOKUP
  const data = context.reduce(
    (acc, tuple) => {
      acc.params.push(tuple[0])
      acc.args.push(tuple[1])
      return acc
    },
    { params: [], args: [] }
  )

  let els = _children_cache.get(container)
  if (!els || !preferCachedChildren) {
    els = [container].concat(Array.from(container.querySelectorAll(`${SUPPORTED_KEYS.map(x => `[${x}]`).join(', ')}`)))
    _children_cache.set(container, els)
  }

  els.forEach(el => {
    const condFnString = el.getAttribute('x-if')
    if (condFnString) {
      const condFnCacheKey = `__fn_x-if`
      let condFn = el[condFnCacheKey]
      if (condFnString && !condFn) {
        condFn = new Function('$el', '$SKIP', '$cqs', ...data.params, `return (${condFnString})`)
        el[condFnCacheKey] = condFn
      }

      if (condFn) {
        const shouldContinue = condFn(el, _HYDRATE_SKIP, $cqs, ...data.args)
        if (!shouldContinue) return
      }
    }

    for (const fnAttr in fnAttrsLookup) {
      if (!Object.prototype.hasOwnProperty.call(fnAttrsLookup, fnAttr)) {
        continue
      }
      const condAttr = `${fnAttr}-if`

      const ourEffect = fnAttrsLookup[fnAttr]
      const userFnString = el.getAttribute(fnAttr)
      const condFnString = el.getAttribute(condAttr)

      if (!userFnString) {
        continue
      }

      const fnCacheKey = `__fn_${fnAttr}`
      const condFnCacheKey = `__fn_${condAttr}`

      let userFn = el[fnCacheKey]

      if (!userFn) {
        // if there is our effect, then we expect the `userFn` to be returnable
        const returnable = !!ourEffect
        const fnbody = returnable ? `return (${userFnString})` : userFnString
        userFn = new Function('$el', '$SKIP', '$cqs', ...data.params, fnbody)
        el[fnCacheKey] = userFn
      }

      let condFn = el[condFnCacheKey]
      if (condFnString && !condFn) {
        condFn = new Function('$el', '$SKIP', '$cqs', ...data.params, `return (${condFnString})`)
        el[condFnCacheKey] = condFn
      }

      try {
        if (condFn) {
          const shouldContinue = condFn(el, _HYDRATE_SKIP, $cqs, ...data.args)
          if (!shouldContinue) continue
        }

        // Run user fn
        const result = userFn(el, _HYDRATE_SKIP, $cqs, ...data.args)
        if (result === _HYDRATE_SKIP) continue

        // Run effect - if any
        if (ourEffect) {
          ourEffect(el, result)
        }
      } catch (e) {
        if (!el.hasAttribute('x-no-err')) {
          console.error('[hydrateFromHtml] :: ', e.message)
        }
      }
    }
  })
}

// ---------------------------------------------------------------

export class BaseElement extends HTMLElement {
  constructor() {
    super()
    this._connected = false
    /** @type {AbortController} */
    this._ac = /** @type {any} */ (null)
  }

  async connectedCallback() {
    this._connected = true
    await Promise.resolve()
    if (!this._connected) return

    this._ac && !this._ac.signal.aborted && this._ac.abort()
    this._ac = new AbortController()

    /** @type {any} */
    let x = this.mount()
    if (x instanceof Promise) {
      x = await x
    }

    if (x !== false) {
      this._aftermount()
    }
  }

  mount() {}
  unmount() {}

  _aftermount() {
    this.setAttribute('hydrated', 'true')
  }
  _afterunmount() {
    this.setAttribute('hydrated', 'false')
  }

  async disconnectedCallback() {
    this._connected = false
    await Promise.resolve()
    if (this._connected) return

    this._ac && !this._ac.signal.aborted && this._ac.abort()
    // @ts-expect-error
    this._ac = null
    this.unmount()
    this._afterunmount()
  }
}
// ---------------------------------------------------------------

export function formatMoney(...args) {
  return window.formatMoney.apply(null, args)
}